import { useEffect, useState } from "react";

import {
    ListContainer,
    ListContext,
    Page,
    PageRoute,
    SearchBar,
    buildTemplatePath,
} from "@compass/core";

import { ListFooter } from "components";

import { ListFilterBase, Permission, ScholarshipPaymentMethodListDto, ScholarshipPaymentMethodListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { ScholarshipPaymentMethodTable } from "./ScholarshipPaymentMethodTable";
import { ScholarshipPaymentMethodListPageHeader } from "./ScholarshipPaymentMethodListPageHeader";

const routeName = "scholarshipPaymentMethod";

export const scholarshipPaymentMethodListPageRoute: PageRoute = {
    handle: "Scholarship Payment Method",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const ScholarshipPaymentMethodListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;

    const [listItems, setListItems] = useState<ScholarshipPaymentMethodListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });

    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<ScholarshipPaymentMethodListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.scholarshipPaymentMethod.getAll();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.scholarshipPaymentMethod.getAll();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.scholarshipPaymentMethod.getListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<ScholarshipPaymentMethodListPageHeader rootPermission={ROOT_PERMISSION} handle={scholarshipPaymentMethodListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <ScholarshipPaymentMethodTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            scholarshipPaymentMethod={listItems}
                            setScholarshipPaymentMethod={setListItems} 
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};
